import React, { useState, useEffect, useCallback } from 'react';
import { Download, Search, CheckCircle, FileText } from 'lucide-react';
import { quoteService } from '../services/quoteService';
import { fragebogenService } from '../services/fragebogenService';
import type { Quote, QuoteFilters, QuoteStatus, Fragebogen } from '../types/quotes';

const QuoteDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'quotes' | 'fragebogen'>('quotes');
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [fragebogenList, setFragebogenList] = useState<Fragebogen[]>([]);
  const [filters, setFilters] = useState<QuoteFilters>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { quotes: fetchedQuotes } = await quoteService.listQuotes(filters);
      setQuotes(fetchedQuotes);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadFragebogen = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedFragebogen = await fragebogenService.listFragebogen();
      setFragebogenList(fetchedFragebogen);
    } catch (error) {
      console.error('Error loading fragebogen:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'quotes') {
      loadQuotes();
    } else {
      loadFragebogen();
    }
  }, [activeTab, loadQuotes, loadFragebogen]);

  const handleExport = () => {
    const csv = quoteService.exportToCSV(quotes);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `angebote-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleStatusFilter = (status: string) => {
    if (status === '') {
      setFilters({ ...filters, status: undefined });
    } else {
      setFilters({ ...filters, status: [status as QuoteStatus] });
    }
  };

  const getStatusBadge = (status: QuoteStatus) => {
    const styles = {
      draft: 'bg-gray-200 text-gray-800',
      submitted: 'bg-blue-200 text-blue-800',
      in_progress: 'bg-yellow-200 text-yellow-800',
      completed: 'bg-green-200 text-green-800',
      cancelled: 'bg-gray-200 text-gray-800'
    };
    
    const labels = {
      draft: 'Entwurf',
      submitted: 'Eingereicht',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-sm font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const filteredQuotes = quotes.filter(q => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      q.clientName.toLowerCase().includes(search) ||
      q.projectName?.toLowerCase().includes(search) ||
      q.id.toLowerCase().includes(search) ||
      q.clientEmail?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Kunden-Dashboard</h1>
        {activeTab === 'quotes' && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download size={20} />
            CSV Exportieren
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('quotes')}
          className={`px-6 py-3 font-medium transition-colors border-b-2 ${
            activeTab === 'quotes'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <FileText size={20} />
            Angebote ({quotes.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('fragebogen')}
          className={`px-6 py-3 font-medium transition-colors border-b-2 ${
            activeTab === 'fragebogen'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle size={20} />
            Fragebögen ({fragebogenList.length})
          </div>
        </button>
      </div>

      {activeTab === 'quotes' ? (
        <>
          {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Suchen (Kunde, ID, E-Mail)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.status?.[0] || ''}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Alle Status</option>
            <option value="submitted">Eingereicht</option>
            <option value="in_progress">In Bearbeitung</option>
            <option value="completed">Abgeschlossen</option>
            <option value="cancelled">Storniert</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredQuotes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>Keine Angebote gefunden.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projektname
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Betrag
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fragebogen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Von
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuotes.map((quote) => (
                  <tr 
                    key={quote.id} 
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${quote.status === 'cancelled' ? 'opacity-60' : ''}`}
                    onClick={() => {
                      window.location.href = `/quotes/${quote.id}`;
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {quote.id.substring(0, 8)}...
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${quote.status === 'cancelled' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      <div className="flex items-center gap-2">
                        <span>{quote.projectName || '-'}</span>
                        {quote.status === 'cancelled' && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                            Storniert
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        quote.isCustomerQuote 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {quote.isCustomerQuote ? 'Kunde' : 'Admin'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      €{quote.amountTotal.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(quote.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {quote.fragebogenSubmitted ? (
                        <CheckCircle className="w-5 h-5 text-green-600 inline" />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {quote.createdAt.toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {quote.createdByUserName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      ) : (
        // Fragebogen Tab
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {fragebogenList.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>Keine Fragebögen gefunden.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {fragebogenList.map((item) => (
                <div
                  key={item.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {item.projektName || 'Unbenanntes Projekt'}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>
                          <strong>Kontakt:</strong> {item.kontaktName} ({item.kontaktEmail})
                        </p>
                        {item.domain && (
                          <p>
                            <strong>Domain:</strong> {item.domain}
                          </p>
                        )}
                        <p>
                          <strong>Zielgruppe:</strong> {item.zielgruppe}
                        </p>
                        <p>
                          <strong>Eingereicht:</strong>{' '}
                          {item.submittedAt.toLocaleDateString('de-DE')} von {item.submittedByUserName}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col gap-2">
                      {item.quoteId && (
                        <button
                          onClick={() => window.location.href = `/quotes/${item.quoteId}`}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          → Zum Angebot
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteDashboard;

